import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatGHS, formatApiErrorDetail, formatCarName } from "@/lib/api";
import {
  Users,
  Fuel,
  Settings,
  Briefcase,
  Loader2,
  Check,
  CheckCircle2,
  Calendar,
  MapPin,
  Phone,
  Mail,
  User,
  ArrowRight,
  ArrowLeft,
  MessageSquare,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import SEO, { carSchema, breadcrumbSchema } from "@/components/SEO";

const LOCATIONS = [
  "East Legon (HQ)",
  "Kotoka International Airport",
  "Accra Mall",
  "Cantonments",
  "Tema",
  "Kumasi",
  "Takoradi",
];

const ACCRA_LOCATIONS = new Set([
  "East Legon (HQ)",
  "Kotoka International Airport",
  "Accra Mall",
  "Cantonments",
]);

function isOutsideAccra(pickup, drop) {
  return !ACCRA_LOCATIONS.has(pickup) || !ACCRA_LOCATIONS.has(drop);
}

function diffDays(a, b) {
  if (!a || !b) return 1;
  const d = (new Date(b) - new Date(a)) / 86400000;
  return Math.max(1, Math.round(d));
}

export default function CarDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);

  const [step, setStep] = useState(1); // 1 = trip, 2 = driver, 3 = review
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // {reference, total} after submit

  const [form, setForm] = useState({
    pickup_location: params.get("pickup") || LOCATIONS[0],
    return_location: params.get("dropoff") || LOCATIONS[0],
    pickup_date: params.get("pickup_date") || new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    return_date: params.get("return_date") || new Date(Date.now() + 4 * 86400000).toISOString().slice(0, 10),
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    driver_license: "",
    notes: "",
  });

  useEffect(() => {
    setLoading(true);
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setCar(false)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#e63946]" size={32} /></div>;
  if (!car) return <div className="container-titan py-20 text-center">Car not found.</div>;

  const days = diffDays(form.pickup_date, form.return_date);
  const outside = isOutsideAccra(form.pickup_location, form.return_location);
  const daily = outside && car.daily_rate_outside ? car.daily_rate_outside : car.daily_rate;
  const total = days * daily;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goNext = () => {
    if (step === 1) {
      if (!form.pickup_date || !form.return_date) { toast.error("Pick your dates"); return; }
      if (diffDays(form.pickup_date, form.return_date) < 1) { toast.error("Return must be after pickup"); return; }
      setStep(2);
    } else if (step === 2) {
      if (!form.customer_name || !form.customer_email || !form.customer_phone) {
        toast.error("Fill in your full name, email and phone");
        return;
      }
      setStep(3);
    }
  };

  // Compose the full booking summary that auto-forwards to the team's WhatsApp.
  const buildWhatsAppUrl = (ref) => {
    const lines = [
      `📩 *New booking request* — Titan Car Rentals`,
      ``,
      `*Reference:* ${ref}`,
      `*Car:* ${formatCarName(car)}`,
      `*Pickup:* ${form.pickup_location} · ${form.pickup_date}`,
      `*Return:* ${form.return_location} · ${form.return_date}`,
      `*Days:* ${days} (${outside ? "Outside Accra" : "Within Accra"})`,
      `*Estimate:* GH₵${total.toLocaleString("en-GH")}`,
      ``,
      `*Customer:* ${form.customer_name}`,
      `*Phone:* ${form.customer_phone}`,
      `*Email:* ${form.customer_email}`,
      form.driver_license ? `*Licence:* ${form.driver_license}` : null,
      form.notes ? `*Notes:* ${form.notes}` : null,
      ``,
      `Please confirm availability and let me know how to pay. Thanks!`,
    ].filter(Boolean).join("\n");
    return `https://wa.me/233244945582?text=${encodeURIComponent(lines)}`;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, car_id: car.id };
      const { data } = await api.post("/bookings", payload);
      setConfirmed({ reference: data.reference, total: data.amount, days: data.days });
      // NOTE: do NOT auto-open WhatsApp here. Browsers block window.open() when it's
      // not inside a direct click handler (which this one isn't — it's after an async
      // POST). The customer taps the big green "Send booking on WhatsApp" button on
      // the success screen, which IS a direct click → opens WhatsApp reliably with
      // the pre-filled booking summary ready to send.
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(confirmed.reference);
      toast.success("Reference copied");
    } catch (_) {
      toast.error("Could not copy");
    }
  };

  return (
    <div data-testid="car-detail-page">
      <SEO
        title={`${formatCarName(car)} — Rent in Ghana from GH₵${car.daily_rate}/day`}
        description={`Hire a ${formatCarName(car)} in Accra, Ghana with Titan Car Rentals. ${car.category} · ${car.seats || 5} seats · ${car.transmission || "Automatic"} · ${car.fuel || "Petrol"}. From GH₵${car.daily_rate}/day. Self-drive or chauffeur. Book online.`}
        path={`/cars/${car.id}`}
        image={car.image_url}
        type="product"
        jsonLd={[
          carSchema(car),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Fleet", path: "/fleet" },
            { name: formatCarName(car), path: `/cars/${car.id}` },
          ]),
        ]}
      />
      {/* Header */}
      <section className="bg-[#0d2040] text-white py-12">
        <div className="container-titan">
          <button onClick={() => nav(-1)} className="text-white/70 hover:text-white text-xs uppercase tracking-widest flex items-center gap-2 mb-4">
            <ArrowLeft size={14} /> Back to fleet
          </button>
          <span className="eyebrow text-[#ff8a93]">{car.category}</span>
          <h1 className="h-section mt-2 text-white">{formatCarName(car)}</h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-titan grid lg:grid-cols-12 gap-10">
          {/* Left: Car info */}
          <div className="lg:col-span-7">
            <div className="aspect-[16/10] bg-[#f7f8fa] overflow-hidden">
              <img src={car.image_url} alt={formatCarName(car)} className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              {[
                { icon: Users, label: "Seats", value: car.seats },
                { icon: Settings, label: "Transmission", value: car.transmission },
                { icon: Fuel, label: "Fuel", value: car.fuel },
                { icon: Briefcase, label: "Luggage", value: car.luggage },
              ].map((s) => (
                <div key={s.label} className="border border-gray-200 p-4">
                  <s.icon size={16} className="text-[#e63946] mb-2" />
                  <div className="text-xs uppercase tracking-widest text-gray-500">{s.label}</div>
                  <div className="font-display text-lg font-semibold text-[#0d2040] mt-1 capitalize">{s.value}</div>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <h3 className="font-display text-2xl font-semibold text-[#0d2040]">About this car</h3>
              <p className="text-gray-700 leading-relaxed mt-3">{car.description}</p>
            </div>
            {car.features?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-display text-xl font-semibold text-[#0d2040] mb-4">Features</h3>
                <ul className="grid grid-cols-2 gap-y-2">
                  {car.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-[#0d2040]">
                      <Check size={14} className="text-[#e63946]" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right: Booking flow */}
          <aside className="lg:col-span-5">
            <div className="border border-gray-200 bg-white booking-widget sticky top-24" data-testid="booking-flow">
              <div className="p-5 border-b border-gray-200 bg-[#0d2040] text-white">
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-lg">Reserve this car</span>
                  <div className="text-right">
                    <div className="text-2xl font-display font-bold text-[#ff8a93]">{formatGHS(daily)}</div>
                    <div className="text-[0.65rem] uppercase tracking-widest text-white/60">
                      per day · {outside ? "outside Accra" : "within Accra"}
                    </div>
                  </div>
                </div>
                {/* Step indicator */}
                <div className="flex items-center gap-2 mt-4">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className="flex-1 h-1 bg-white/15">
                      <div className={`h-full bg-[#e63946] transition-all ${step >= s ? "w-full" : "w-0"}`} />
                    </div>
                  ))}
                </div>
                <div className="text-xs uppercase tracking-widest text-white/60 mt-2">
                  Step {step} of 3 · {step === 1 ? "Trip details" : step === 2 ? "Driver details" : "Review & pay"}
                </div>
              </div>

              <div className="p-5">
                {step === 1 && (
                  <div className="space-y-4" data-testid="booking-step-1">
                    <div>
                      <label className="titan-label"><MapPin size={12} className="inline mr-1" /> Pickup location</label>
                      <select className="titan-input" value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} data-testid="step1-pickup-location">
                        {LOCATIONS.map((l) => <option key={l}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="titan-label"><MapPin size={12} className="inline mr-1" /> Return location</label>
                      <select className="titan-input" value={form.return_location} onChange={(e) => update("return_location", e.target.value)} data-testid="step1-return-location">
                        {LOCATIONS.map((l) => <option key={l}>{l}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="titan-label"><Calendar size={12} className="inline mr-1" /> Pickup</label>
                        <input type="date" className="titan-input" value={form.pickup_date} min={new Date().toISOString().slice(0,10)} onChange={(e) => update("pickup_date", e.target.value)} data-testid="step1-pickup-date" />
                      </div>
                      <div>
                        <label className="titan-label"><Calendar size={12} className="inline mr-1" /> Return</label>
                        <input type="date" className="titan-input" value={form.return_date} min={form.pickup_date} onChange={(e) => update("return_date", e.target.value)} data-testid="step1-return-date" />
                      </div>
                    </div>
                    <div className="border-t border-gray-100 pt-3 flex items-center justify-between text-sm">
                      <span className="text-gray-500">{days} day{days > 1 ? "s" : ""} × {formatGHS(daily)}</span>
                      <span className="font-display text-xl font-bold text-[#0d2040]">{formatGHS(total)}</span>
                    </div>
                    <button className="btn-primary w-full" onClick={goNext} data-testid="step1-next-btn">
                      Continue <ArrowRight size={14} />
                    </button>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4" data-testid="booking-step-2">
                    <div>
                      <label className="titan-label"><User size={12} className="inline mr-1" /> Full name</label>
                      <input className="titan-input" placeholder="Kwame Mensah" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="step2-name" />
                    </div>
                    <div>
                      <label className="titan-label"><Mail size={12} className="inline mr-1" /> Email</label>
                      <input type="email" className="titan-input" placeholder="you@email.com" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="step2-email" />
                    </div>
                    <div>
                      <label className="titan-label"><Phone size={12} className="inline mr-1" /> Phone</label>
                      <input className="titan-input" placeholder="054 000 0000" value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} data-testid="step2-phone" />
                    </div>
                    <div>
                      <label className="titan-label">Driver&apos;s license # (optional)</label>
                      <input className="titan-input" value={form.driver_license} onChange={(e) => update("driver_license", e.target.value)} data-testid="step2-license" />
                    </div>
                    <div>
                      <label className="titan-label">Notes / requests (optional)</label>
                      <textarea rows={2} className="titan-input" value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="step2-notes" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button className="btn-outline-navy" onClick={() => setStep(1)}><ArrowLeft size={14} /> Back</button>
                      <button className="btn-primary" onClick={goNext} data-testid="step2-next-btn">Review <ArrowRight size={14} /></button>
                    </div>
                  </div>
                )}

                {step === 3 && !confirmed && (
                  <div className="space-y-4" data-testid="booking-step-3">
                    <h4 className="font-display text-lg font-semibold text-[#0d2040]">Review your booking</h4>
                    <div className="border border-gray-200 p-4 text-sm space-y-2">
                      <Row label="Car" value={formatCarName(car)} />
                      <Row label="Pickup" value={`${form.pickup_location} · ${form.pickup_date}`} />
                      <Row label="Return" value={`${form.return_location} · ${form.return_date}`} />
                      <Row label="Renter" value={form.customer_name} />
                      <Row label="Email" value={form.customer_email} />
                      <Row label="Phone" value={form.customer_phone} />
                      <div className="border-t border-gray-100 mt-2 pt-2 flex items-center justify-between">
                        <span className="text-gray-500">{days} day{days > 1 ? "s" : ""} × {formatGHS(daily)} ({outside ? "outside Accra" : "within Accra"})</span>
                        <span className="font-display text-2xl font-bold text-[#e63946]">{formatGHS(total)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Submit your booking request and our team will call you shortly on{" "}
                      <span className="font-semibold text-[#0d2040]">{form.customer_phone || "your number"}</span> to
                      confirm availability and arrange payment (Mobile Money or bank transfer).
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <button className="btn-outline-navy" onClick={() => setStep(2)} disabled={submitting}><ArrowLeft size={14} /> Back</button>
                      <button className="btn-primary" onClick={submit} disabled={submitting} data-testid="step3-pay-btn">
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Submit booking <ArrowRight size={14} /></>}
                      </button>
                    </div>
                  </div>
                )}

                {step === 3 && confirmed && (
                  <div className="space-y-4 text-center" data-testid="booking-confirmed">
                    <CheckCircle2 className="mx-auto text-green-600" size={48} />
                    <h4 className="font-display text-2xl font-bold text-[#0d2040]">Booking received!</h4>
                    <p className="text-sm text-gray-600">
                      Thank you, {form.customer_name.split(" ")[0]}. Our team will contact you on{" "}
                      <span className="font-semibold text-[#0d2040]">{form.customer_phone}</span> within a few hours
                      during business hours to confirm and take payment.
                    </p>
                    <div className="inline-block border border-gray-200 bg-[#f7f8fa] px-5 py-3">
                      <div className="text-[0.65rem] uppercase tracking-widest text-gray-500">Your reference</div>
                      <div className="flex items-center justify-center gap-2 mt-1">
                        <div className="font-display text-2xl font-bold text-[#e63946]" data-testid="booking-reference">{confirmed.reference}</div>
                        <button
                          type="button"
                          onClick={copyRef}
                          className="text-[0.65rem] uppercase tracking-widest text-gray-500 hover:text-[#0d2040] border border-gray-300 px-2 py-1"
                          data-testid="booking-reference-copy"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>Total estimate: <span className="font-bold text-[#0d2040]">{formatGHS(confirmed.total)}</span> ({confirmed.days} day{confirmed.days > 1 ? "s" : ""})</div>
                    </div>
                    {/* --- CRITICAL: this is how the booking actually reaches the team ---
                        We DO NOT auto-open WhatsApp (browsers block it). The customer
                        must TAP this button for the booking summary to land in our
                        WhatsApp inbox. Keep it prominent, keep the warning banner above. */}
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-3 text-left text-xs text-amber-900 font-semibold mt-2" data-testid="booking-whatsapp-notice">
                      ⚠️ One last step — tap below to send us your booking on WhatsApp so our team sees it instantly.
                    </div>
                    <a
                      href={buildWhatsAppUrl(confirmed.reference)}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1da851] text-white px-6 py-5 text-base font-bold uppercase tracking-wider w-full transition-colors shadow-lg hover:shadow-xl"
                      data-testid="booking-whatsapp-cta"
                    >
                      <MessageSquare size={22} /> Send booking on WhatsApp
                    </a>
                    <div className="text-[0.7rem] text-gray-500">
                      WhatsApp will open with your booking details pre-filled — just tap <span className="font-semibold">Send</span>.
                    </div>
                    <div className="text-xs text-gray-500 pt-1">
                      Not on WhatsApp? Call <a href="tel:+233540124247" className="text-[#0d2040] font-semibold hover:text-[#e63946]">054 012 4247</a> and quote your reference above.
                    </div>
                    <Link to="/fleet" className="btn-outline-navy w-full justify-center">Browse more cars</Link>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-gray-500 text-xs uppercase tracking-widest">{label}</span>
      <span className="text-[#0d2040] font-medium text-right">{value}</span>
    </div>
  );
}
